import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './nakshatra.css';

/* ─── Constants ──────────────────────────────────────────────────── */
const NK_SYMBOLS = {
  Ashwini: '🐴', Bharani: '🔺', Krittika: '🔥', Rohini: '🛒',
  Mrigashira: '🦌', Ardra: '💧', Punarvasu: '🏹', Pushya: '🌸',
  Ashlesha: '🐍', Magha: '👑', 'Purva Phalguni': '🛏️', 'Uttara Phalguni': '🛋️',
  Hasta: '✋', Chitra: '💎', Swati: '🌬️', Vishakha: '🚪',
  Anuradha: '🪷', Jyeshtha: '💍', Mula: '🌳', 'Purva Ashadha': '🌊',
  'Uttara Ashadha': '🐘', Shravana: '👂', Dhanishta: '🥁', Shatabhisha: '⭕',
  'Purva Bhadrapada': '⚔️', 'Uttara Bhadrapada': '👥', Revati: '🐟',
};

const HEADER_H = 52; // AppLayout header height

/* ─── Helpers ────────────────────────────────────────────────────── */
const fmtIN = (n) =>
  n == null || isNaN(n)
    ? '0.00'
    : parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayStr = () => new Date().toISOString().split('T')[0];

/* ─── StatPill ───────────────────────────────────────────────────── */
function StatPill({ icon, value, label, color }) {
  return (
    <div style={{ ...S.statPill, borderColor: color + '44' }}>
      <span style={{ ...S.statDot, background: color }}>{icon}</span>
      <div>
        <div style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
      </div>
    </div>
  );
}

/* ─── SortableTable ──────────────────────────────────────────────── */
function SortableTable({ rows, setRows, isPositive, minCs }) {
  const [dirs, setDirs]     = useState({});
  const [copied, setCopied] = useState(false);

  const copySymbols = () => {
    if (!rows.length) return;
    const text = rows.map(s => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sort = (col) => {
    setDirs((prev) => {
      const dir = prev[col] === 'asc' ? 'desc' : 'asc';
      setRows((r) =>
        [...r].sort((a, b) => {
          const av = a[col], bv = b[col];
          if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
          return dir === 'asc' ? av - bv : bv - av;
        })
      );
      return { ...prev, [col]: dir };
    });
  };

  const arrow = (col) => (dirs[col] === 'asc' ? ' ▲' : dirs[col] === 'desc' ? ' ▼' : ' ⇅');

  const COLS = [
    { key: 'symbol',   label: 'Symbol',       w: '18%' },
    { key: 'nSector',  label: 'Sector',        w: '28%' },
    { key: 'csValue',  label: 'CS Value (L)',  w: '18%' },
    { key: 'iep',      label: 'IEP ₹',         w: '16%' },
    { key: 'pctChng',  label: '% Chg',         w: '10%' },
    { key: 'chng',     label: 'Chg ₹',         w: '10%' },
  ];

  return (
    <div className="nk-table-card" style={S.tableCard}>
      {/* ── Table heading ── */}
      <div className="nk-table-head" style={S.tableHead}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            ...S.badge,
            background: isPositive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${isPositive ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            color: isPositive ? '#10b981' : '#ef4444',
          }}>
            {isPositive ? '▲ POSITIVE' : '▼ NEGATIVE'}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>
            {isPositive ? `CS > ${minCs} L` : 'CS < −10 L'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="nk-copy-btn"
            onClick={copySymbols}
            disabled={!rows.length}
            title={`Copy all ${isPositive ? 'positive' : 'negative'} symbols for TradingView`}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: '0.68rem', fontWeight: 600,
              padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
              border: copied
                ? `1px solid ${isPositive ? '#10b981' : '#ef4444'}`
                : `1px solid ${isPositive ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
              background: copied
                ? `${isPositive ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`
                : `${isPositive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'}`,
              color: copied
                ? (isPositive ? '#6ee7b7' : '#fca5a5')
                : (isPositive ? '#10b981' : '#ef4444'),
              transition: 'all 0.2s',
              opacity: rows.length ? 1 : 0.4,
            }}
          >
            {copied ? '✓ Copied!' : '⧉ Copy'}
          </button>
          <span style={S.countBadge}>{rows.length} stocks</span>
        </div>
      </div>

      {/* ── Column headers ── */}
      <div className="nk-col-header-row" style={S.colHeaderRow}>
        {COLS.map((c) => (
          <div className="nk-col-header" key={c.key} style={{ ...S.colHeader, width: c.w }} onClick={() => sort(c.key)}>
            {c.label}<span style={{ opacity: 0.5, fontSize: '0.6rem' }}>{arrow(c.key)}</span>
          </div>
        ))}
      </div>

      {/* ── Rows ── */}
      <div className="nk-rows-area" style={S.rowsArea}>
        {rows.length === 0 ? (
          <div style={S.emptyState}>
            <span style={{ fontSize: '2rem', opacity: 0.3 }}>📭</span>
            <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem' }}>
              No matching stocks
            </p>
          </div>
        ) : (
          rows.map((stock, i) => {
            const up = stock.pctChng > 0;
            const dn = stock.pctChng < 0;
            const cc = up ? '#10b981' : dn ? '#ef4444' : 'rgba(255,255,255,0.45)';
            return (
              <div className="nk-data-row" key={i} style={{ ...S.dataRow, background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                <div className="nk-cell nk-cell-symbol" style={{ ...S.cell, width: COLS[0].w, color: '#a78bfa', fontWeight: 700, fontSize: '0.82rem' }}>
                  {stock.symbol}
                </div>
                <div className="nk-cell nk-cell-sector" style={{ ...S.cell, width: COLS[1].w }}>
                  <span style={S.sectorBadge}>{stock.nSector}</span>
                </div>
                <div className="nk-cell nk-cell-cs" style={{ ...S.cell, width: COLS[2].w, fontFamily: 'monospace', fontWeight: 600 }}>
                  {fmtIN(stock.csValue)}
                </div>
                <div className="nk-cell nk-cell-iep" style={{ ...S.cell, width: COLS[3].w }}>₹{fmtIN(stock.iep)}</div>
                <div className="nk-cell nk-cell-pct" style={{ ...S.cell, width: COLS[4].w, color: cc, fontWeight: 700 }}>
                  {up ? '+' : ''}{stock.pctChng?.toFixed(2)}%
                </div>
                <div className="nk-cell nk-cell-chg" style={{ ...S.cell, width: COLS[5].w, color: cc }}>
                  {up ? '+' : ''}{fmtIN(stock.chng)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function Nakshatra() {
  const [date, setDate]         = useState(todayStr());
  const [minCs, setMinCs]       = useState('100');
  const [sortBy, setSortBy]     = useState('csValue');
  const [loading, setLoading]   = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [posRows, setPosRows]   = useState([]);
  const [negRows, setNegRows]   = useState([]);
  const timer = useRef(null);

  /* stars */
  const stars = useRef(
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: `${(Math.random() * 4).toFixed(2)}s`,
      size: Math.random() > 0.8 ? 3 : 2,
    }))
  ).current;

  const fetchData = useCallback(async () => {
    setSpinning(true);
    setLoading(true);
    setError('');
    try {
      const p = new URLSearchParams({ minCsValue: minCs || 100, minNegativeCsValue: -10, sortBy });
      if (date) p.append('date', date);
      const res = await api.get(`/nakshatra-stocks/current?${p}`);
      if (res.data.success) {
        setData(res.data);
        setPosRows(res.data.positiveStocks || []);
        setNegRows(res.data.negativeStocks || []);
      } else throw new Error(res.data.error || 'Unknown error');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to fetch');
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, [minCs, sortBy, date]);

  useEffect(() => {
    fetchData();
    timer.current = setInterval(() => {
      if (date === todayStr()) fetchData();
    }, 5 * 60 * 1000);
    return () => clearInterval(timer.current);
  }, [fetchData]);

  const exportCSV = () => {
    if (!data) return;
    const all = [...posRows, ...negRows];
    if (!all.length) { alert('No data to export'); return; }
    const hdr = ['Symbol','Stock Name','Sector','Type','CS Value (L)','IEP','Change','% Change','Status'];
    const rows = all.map((s) =>
      [s.symbol, s.stockName, s.nSector, s.nType,
       s.csValue?.toFixed(2), s.iep?.toFixed(2),
       s.chng?.toFixed(2), s.pctChng?.toFixed(2), s.adu
      ].map((v) => `"${v ?? ''}"`)
    );
    const csv = [hdr.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `nakshatra-${data?.nakshatra?.name ?? 'stocks'}-${todayStr()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const nk  = data?.nakshatra;
  const smry = data?.summary;
  const sym  = nk ? (NK_SYMBOLS[nk.name] ?? '⭐') : '⭐';
  const dateLabel = data
    ? (data.selectedDate
        ? new Date(data.selectedDate).toLocaleDateString('en-IN')
        : new Date(data.timestamp).toLocaleDateString('en-IN'))
    : '--';

  return (
    <AppLayout pageBadge="Nakshatra">

      {/* ── starfield ── */}
      <div style={S.starfield}>
        {stars.map((s) => (
          <div key={s.id} style={{
            ...S.star,
            left: s.left, top: s.top,
            animationDelay: s.delay,
            width: s.size, height: s.size,
          }} />
        ))}
      </div>

      {/* ── full-viewport shell (no page scroll) ── */}
      <div className="nk-shell" style={{ ...S.shell, marginTop: HEADER_H, height: `calc(100vh - ${HEADER_H}px)` }}>

        {/* ══ ROW 1 — Controls bar ══════════════════════════════ */}
        <div className="nk-controls-bar" style={S.controlsBar}>
          <div className="nk-bar-left" style={S.barLeft}>
            <span className="nk-page-title" style={S.pageTitle}>⭐ Nakshatra Stock Analysis</span>
          </div>
          <div className="nk-bar-right" style={S.barRight}>
            <input type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              className="nk-input"
              style={S.inp} />
            <input type="number" placeholder="Min CS (L)" value={minCs}
              onChange={(e) => setMinCs(e.target.value)}
              onBlur={fetchData}
              className="nk-input"
              style={{ ...S.inp, width: 120 }} />
            <select className="nk-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={S.inp}>
              <option value="csValue">Sort: CS Value</option>
              <option value="pctChng">Sort: % Change</option>
              <option value="valueLakhs">Sort: Value</option>
            </select>
            <button className="nk-btn nk-btn-primary" onClick={fetchData} disabled={spinning} style={S.btnPrimary}>
              <span style={spinning ? S.spinning : undefined}>🔄</span> Refresh
            </button>
            <button className="nk-btn nk-btn-green" onClick={exportCSV} style={S.btnGreen}>
              ⬇ Export
            </button>
          </div>
        </div>

        {/* ══ ROW 2 — Nakshatra details + Stats combined ══════════ */}
        <div className="nk-info-row" style={S.infoRow}>
          {/* Nakshatra card section */}
          <div className="nk-card" style={S.nkCard}>
            <div style={S.nkSymbolBox}>{sym}</div>
            <div style={S.nkCardBody}>
              <div style={S.nkCardName}>{nk?.name ?? 'Loading…'}</div>
              <div style={S.nkCardMeta}>
                <span style={S.nkMetaItem}><span style={S.nkMetaLabel}>Lord</span><span style={S.nkMetaValue}>{nk?.lord ?? '--'}</span></span>
                <span style={S.nkMetaDivider} />
                <span style={S.nkMetaItem}><span style={S.nkMetaLabel}>Rashi</span><span style={S.nkMetaValue}>{nk?.rashi ?? '--'}</span></span>
                <span style={S.nkMetaDivider} />
                <span style={S.nkMetaItem}><span style={S.nkMetaLabel}>Type</span><span style={S.nkMetaValue}>{nk?.nTypes ?? 'N/A'}</span></span>
                <span style={S.nkMetaDivider} />
                <span style={S.nkMetaItem}><span style={S.nkMetaLabel}>Updated</span><span style={S.nkMetaValue}>{dateLabel}</span></span>
              </div>
            </div>
          </div>

          {/* vertical divider */}
          <div className="nk-row-divider" style={S.rowDivider} />

          {/* Stats pills section */}
          <div className="nk-stats-strip" style={S.statsStrip}>
            <StatPill icon="📋" value={data?.totalStocks ?? 0}                                        label="Total"     color="#667eea" />
            <StatPill icon="💰" value={smry ? `₹${fmtIN(smry.totalCsValue)}L` : '₹0'}                label="CS Value"  color="#f59e0b" />
            <StatPill icon="▲"  value={smry?.advance ?? 0}                                            label="Advancing" color="#10b981" />
            <StatPill icon="▼"  value={smry?.decline ?? 0}                                            label="Declining" color="#ef4444" />
            <StatPill icon="%"  value={smry ? `${smry.avgPctChng?.toFixed(2)}%` : '0%'}               label="Avg Chg"   color="#4facfe" />
            <StatPill icon="📅" value={`${posRows.length + negRows.length}`}                          label="Filtered"  color="#a78bfa" />
            {error && <div style={S.errChip}>⚠ {error}</div>}
          </div>
        </div>

        {/* ══ ROW 3 — Tables (flex:1, fill remaining) ══════════ */}
        <div className="nk-tables-row" style={S.tablesRow}>
          {loading && !data ? (
            <div style={S.bigLoader}>
              <div style={S.loaderSpinner} />
              <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                Loading Nakshatra Stocks…
              </p>
            </div>
          ) : (
            <>
              <SortableTable
                rows={posRows}
                setRows={setPosRows}
                isPositive={true}
                minCs={minCs || 100}
              />
              <SortableTable
                rows={negRows}
                setRows={setNegRows}
                isPositive={false}
                minCs={minCs || 100}
              />
            </>
          )}
        </div>

      </div>{/* /shell */}
    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  /* combined info row */
  infoRow: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center',
    gap: 12,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '10px 16px',
    flexWrap: 'wrap',
  },
  rowDivider: {
    width: 1, alignSelf: 'stretch', minHeight: 36,
    background: 'rgba(255,255,255,0.1)',
    flexShrink: 0,
  },

  /* nakshatra card (now inside infoRow, no own background) */
  nkCard: {
    display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
  },
  nkSymbolBox: {
    width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.5rem',
    boxShadow: '0 0 18px rgba(99,102,241,0.45)',
    animation: 'pulse 2s infinite',
  },
  nkCardBody: { display: 'flex', flexDirection: 'column', gap: 4 },
  nkCardName: {
    fontWeight: 700, fontSize: '1rem',
    background: 'linear-gradient(135deg,#a78bfa,#60a5fa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    lineHeight: 1.2,
  },
  nkCardMeta: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  nkMetaItem: { display: 'flex', alignItems: 'center', gap: 4 },
  nkMetaLabel: { fontSize: '0.63rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.7 },
  nkMetaValue: {
    fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)',
    background: 'rgba(255,255,255,0.07)',
    padding: '1px 8px', borderRadius: 6,
  },
  nkMetaDivider: {
    width: 1, height: 14,
    background: 'rgba(255,255,255,0.12)',
    display: 'inline-block',
  },

  /* starfield */
  starfield: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
  },
  star: {
    position: 'absolute', background: '#fff', borderRadius: '50%',
    animation: 'twinkle 3s infinite',
  },

  /* no-scroll shell */
  shell: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    padding: '0 16px 12px',
    gap: 8,
    color: '#fff',
    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
  },

  /* ── row 1: controls bar ── */
  controlsBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 8,
    paddingTop: 10,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    paddingBottom: 8,
  },
  barLeft: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  pageTitle: {
    fontWeight: 700, fontSize: '1rem',
    background: 'linear-gradient(135deg,#a78bfa,#60a5fa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    whiteSpace: 'nowrap',
  },
  nkChip: {
    fontSize: '0.73rem', color: 'rgba(255,255,255,0.6)',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20, padding: '3px 12px',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  nkPipe: { color: 'rgba(255,255,255,0.25)', margin: '0 2px' },
  barRight: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  inp: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff', padding: '5px 10px',
    borderRadius: 8, fontSize: '0.78rem',
    fontFamily: 'inherit', outline: 'none', width: 150,
  },
  btnPrimary: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    border: 'none', borderRadius: 8, color: '#fff',
    padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
  },
  btnGreen: {
    background: 'linear-gradient(135deg,#10b981,#059669)',
    border: 'none', borderRadius: 8, color: '#fff',
    padding: '6px 14px', fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer',
  },
  spinning: { display: 'inline-block', animation: 'spin 0.8s linear infinite' },

  /* ── stats strip (inside infoRow) ── */
  statsStrip: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1,
  },
  statPill: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid',
    borderRadius: 10, padding: '6px 14px',
    minWidth: 110,
  },
  statDot: {
    width: 28, height: 28, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.85rem', flexShrink: 0,
  },
  errChip: {
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.35)',
    borderRadius: 8, padding: '6px 12px',
    fontSize: '0.75rem', color: '#fca5a5',
    maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  /* ── row 3: tables ── */
  tablesRow: {
    flex: 1, minHeight: 0,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  bigLoader: {
    gridColumn: '1 / -1',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: '100%',
  },
  loaderSpinner: {
    width: 48, height: 48,
    border: '4px solid rgba(255,255,255,0.08)',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  /* ── table card ── */
  tableCard: {
    display: 'flex', flexDirection: 'column',
    minHeight: 0,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  tableHead: {
    flexShrink: 0,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.04)',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  badge: {
    fontSize: '0.68rem', fontWeight: 700, letterSpacing: 1,
    padding: '3px 10px', borderRadius: 20,
  },
  countBadge: {
    fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.06)',
    padding: '2px 8px', borderRadius: 10,
  },

  /* column headers */
  colHeaderRow: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center',
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  colHeader: {
    fontSize: '0.63rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: 0.8,
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer', userSelect: 'none',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  /* rows area (internal scroll only) */
  rowsArea: {
    flex: 1, minHeight: 0,
    overflowY: 'auto', overflowX: 'hidden',
  },
  dataRow: {
    display: 'flex', alignItems: 'center',
    padding: '7px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: 'background 0.15s',
  },
  cell: {
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', fontSize: '0.8rem',
    paddingRight: 6,
  },
  sectorBadge: {
    background: 'rgba(139,92,246,0.15)',
    border: '1px solid rgba(139,92,246,0.3)',
    borderRadius: 6, padding: '2px 7px',
    fontSize: '0.68rem',
  },
  emptyState: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: 120, opacity: 0.6,
  },
};
